import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import { useApplicationTray } from "@/lib/applicationTray";
import { titleCase } from "@/lib/format";
import { C } from "@/lib/brand";

// Catalogue-first pivot (Feb 29): consumers only see research now. The
// old HOME (/app dashboard) and BROWSE (/app/browse) surfaces belonged
// to the physician-led model, which is now exclusive to employer
// programs. Bottom nav collapses to three: CATALOGUE (home), PLAN,
// and ME. Members with an active physician-supervised subscription
// still see their rich dashboard via the PLAN tab which routes into
// their protocol / injection toolkit / care team.
const tabs = [
  { to: "/catalogue", label: "CATALOGUE", testId: "tab-catalogue", end: true },
  { to: "/app/plan", label: "PLAN", testId: "tab-plan" },
  { to: "/app/me", label: "ME", testId: "tab-me" },
];

// Routes that take over the full viewport — no AppShell header,
// no bottom nav. The flow renders its own minimal chrome.
const FULLSCREEN_ROUTE_PREFIXES = ["/app/assessment", "/app/checkout", "/app/care-team", "/app/protocols"];

export default function AppShell() {
  const { consumer, bootstrapped, logout } = useAuth();
  // Catalogue-first pivot: RESEARCH tab is gone (CATALOGUE now IS the
  // home tab). `researchAccess` flag no longer gates any nav item; the
  // catalogue is public and any authed member can visit it.
  const visibleTabs = tabs;
  const { count: planCount } = useApplicationTray();
  const navigate = useNavigate();
  const location = useLocation();
  const fullscreen = FULLSCREEN_ROUTE_PREFIXES.some((p) => location.pathname.startsWith(p));
  // iOS Safari pins `position: fixed` elements to the *visual* viewport
  // when the soft keyboard slides up — so the bottom nav rides up over
  // the form instead of staying tucked at the layout-viewport bottom.
  // We watch visualViewport.height: when it shrinks materially below the
  // layout height, a keyboard is up → hide the nav so the user's form
  // gets the full screen.
  const keyboardOpen = useKeyboardOpen();

  useEffect(() => {
    if (bootstrapped && !consumer) navigate("/sign-in", { replace: true });
  }, [bootstrapped, consumer, navigate]);

  if (!bootstrapped || !consumer) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.cream }}>
        <Logo size={28} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: C.cream, paddingBottom: fullscreen ? 0 : 92 }}>
      {/* top app nav (desktop) — hidden on fullscreen routes */}
      {!fullscreen && (
      <header
        data-testid="app-header"
        style={{
          background: C.cream,
          borderBottom: `1px solid ${C.warmGray4}`,
          position: "sticky",
          top: 0,
          zIndex: 30,
        }}
      >
        <div
          style={{
            width: "100%",
            padding: "18px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Link to="/catalogue" data-testid="app-logo-link">
            <Logo size={22} />
          </Link>
          <nav className="hidden md:flex" style={{ gap: 28 }}>
            {visibleTabs.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.end}
                data-testid={`${t.testId}-desktop`}
                style={({ isActive }) => ({
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.2em",
                  color: isActive ? C.ink : C.warmGray,
                  textDecoration: "none",
                  borderBottom: isActive ? `2px solid ${C.peachDeep}` : "2px solid transparent",
                  paddingBottom: 4,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                })}
              >
                {t.label}
                {t.testId === "tab-plan" && planCount > 0 && (
                  <PlanCountBadge count={planCount} variant="desktop" />
                )}
              </NavLink>
            ))}
          </nav>
          <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
            <span
              data-testid="app-header-member-name"
              style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", color: C.ink }}
            >
              {titleCase(consumer.firstName || "MEMBER").toUpperCase()}
            </span>
            <button
              type="button"
              data-testid="app-header-signout-btn"
              onClick={() => {
                // Hard sign-out — clears the JWT (handled by logout())
                // and routes back to /sign-in. The applicationTray's
                // identity-change effect will fire on consumer → null
                // and wipe in-memory items + anon cookie, ensuring the
                // next sign-in on this browser starts clean. Belt &
                // braces against the cross-account leak we just fixed.
                logout();
                navigate("/sign-in", { replace: true });
              }}
              style={{
                background: "transparent",
                border: "none",
                padding: "4px 0",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.18em",
                color: C.warmGray,
                cursor: "pointer",
                textTransform: "uppercase",
                borderBottom: `1.5px solid transparent`,
                transition: "color 160ms ease, border-color 160ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = C.ink;
                e.currentTarget.style.borderBottomColor = C.peachDeep;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = C.warmGray;
                e.currentTarget.style.borderBottomColor = "transparent";
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      )}

      <main className="page-enter">
        <Outlet />
      </main>

      {/* bottom tab bar (mobile) — hidden on fullscreen routes AND when
          the soft keyboard is open (iOS pins fixed elements to the
          visual viewport, otherwise the nav rides up over the form). */}
      {!fullscreen && !keyboardOpen && (
      <nav
        data-testid="app-bottom-nav"
        className="md:hidden"
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          background: C.ink,
          padding: "10px 8px 14px",
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          zIndex: 50,
          borderTop: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {visibleTabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            data-testid={`${t.testId}-mobile`}
            style={({ isActive }) => ({
              flex: 1,
              textAlign: "center",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: "0.18em",
              color: isActive ? C.peach : "rgba(250,246,240,0.55)",
              textDecoration: "none",
              padding: "10px 0",
              borderTop: isActive ? `2px solid ${C.peach}` : "2px solid transparent",
              position: "relative",
            })}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              {t.label}
              {t.testId === "tab-plan" && planCount > 0 && (
                <PlanCountBadge count={planCount} variant="mobile" />
              )}
            </span>
          </NavLink>
        ))}
      </nav>
      )}
    </div>
  );
}

// Detect soft-keyboard open on mobile. Uses VisualViewport — the only
// reliable signal across iOS Safari, Chrome iOS, and Android Chrome.
// When the keyboard is up the visual viewport shrinks; when it's a
// browser chrome change (URL bar collapse) the delta stays small. We
// pick a 150px threshold so a normal toolbar collapse never trips this
// but any keyboard does.
function useKeyboardOpen() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const check = () => {
      const delta = window.innerHeight - vv.height;
      setOpen(delta > 150);
    };
    check();
    vv.addEventListener("resize", check);
    vv.addEventListener("scroll", check);
    return () => {
      vv.removeEventListener("resize", check);
      vv.removeEventListener("scroll", check);
    };
  }, []);
  return open;
}

// Small peach pill carrying the application-tray count. Spec §14 calls this
// out as a backlog item that "completes the mental model" — selections made
// anywhere in the app are reflected on the PLAN tab in real time, telling
// members the Plan tab is the working space rather than a passive summary.
//
// Pulse animation: when the count increments (member just added a tier),
// the badge briefly scales to 1.15 and back over 220ms — a small reward
// confirming the act lit up the Plan tab. Removals stay quiet so members
// don't feel "punished" for trimming their plan.
function PlanCountBadge({ count, variant }) {
  const isMobile = variant === "mobile";
  const [pulse, setPulse] = useState(false);
  const prev = useRef(count);
  useEffect(() => {
    if (count > prev.current) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 240);
      prev.current = count;
      return () => clearTimeout(t);
    }
    prev.current = count;
    return undefined;
  }, [count]);
  return (
    <span
      data-testid="plan-count-badge"
      data-pulsing={pulse ? "true" : undefined}
      aria-label={`${count} program${count === 1 ? "" : "s"} in your plan`}
      style={{
        background: C.peach,
        color: C.peachDark,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: "0.05em",
        lineHeight: 1,
        padding: isMobile ? "3px 6px" : "3px 7px",
        borderRadius: 999,
        minWidth: 18,
        textAlign: "center",
        display: "inline-block",
        transformOrigin: "center",
        transition: "transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1)",
        transform: pulse ? "scale(1.18)" : "scale(1)",
        // Star glyph keeps the brand voice — same eyebrow ★ used everywhere.
      }}
    >
      ★ {count}
    </span>
  );
}
